import { useEffect, useState } from 'react'
import { FiLoader } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'

/**
 * Linha do tempo de um lead da landing.
 *
 * Junta duas fontes que já existiam separadas: o que a pessoa fez na página
 * (`wizard_events` — abriu, avançou etapa, clicou para falar com a líder) e o
 * acompanhamento da líder (`leader_followup_events` — contatou, explicou,
 * respondeu o nudge). Vistas juntas, dá para ver onde o lead parou.
 */

interface Evento { type: string; step?: number | null; created_at?: string; ts?: string; detail?: string | null; actor?: string | null }
interface Followup {
  assignment_id: string; leader_name: string | null; status: string | null
  stage: number | null; contacted: string | null; contacted_at: string | null
  explained: string | null; interest: string | null; converted: number | null
  converted_at: string | null; deadline_at: string | null; nudge_count: number | null
  leader_outcome: string | null; assigned_at: string | null
}

/** Rótulos dos eventos da landing. */
const NA_PAGINA: Record<string, string> = {
  start: 'Abriu a página',
  step: 'Avançou uma etapa',
  cep: 'Informou o CEP',
  complete: 'Concluiu o cadastro',
  abandon: 'Abandonou',
  whatsapp_click: 'Clicou para falar com a líder',
  whatsapp_central: 'Clicou para falar com a central',
}

/** As três etapas do acompanhamento, na ordem do funil. */
const ETAPAS = [
  { chave: 'contacted', titulo: 'Entrou em contato' },
  { chave: 'explained', titulo: 'Explicou como funciona' },
  { chave: 'interest', titulo: 'Demonstrou interesse' },
] as const

const RESPOSTA: Record<string, { texto: string; cor: string }> = {
  yes: { texto: 'sim', cor: 'text-emerald-500' },
  no: { texto: 'não', cor: 'text-red-400' },
  pending: { texto: 'ainda não', cor: 'text-amber-500' },
  unreachable: { texto: 'não conseguiu contato', cor: 'text-red-400' },
}

const quando = (d?: string | null) =>
  d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''

export default function LeadTimeline({ sessionId }: { sessionId: string }) {
  const { authenticatedFetch } = useAuth()
  const [dados, setDados] = useState<{ eventos: Evento[]; followup: Followup | null; etapas: Evento[] } | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    authenticatedFetch(`/api/wizard/timeline/${sessionId}`)
      .then(r => r.json())
      .then(j => { if (vivo) (j?.error ? setErro(j.error) : setDados(j)) })
      .catch(() => { if (vivo) setErro('falha ao carregar') })
    return () => { vivo = false }
  }, [authenticatedFetch, sessionId])

  if (erro) return <p className="px-3 py-2 text-[11px] text-red-400">Não consegui carregar a linha do tempo.</p>
  if (!dados) {
    return (
      <p className="flex items-center gap-1.5 px-3 py-3 text-[11px] text-muted-foreground">
        <FiLoader className="h-3 w-3 animate-spin" /> Carregando…
      </p>
    )
  }

  const f = dados.followup

  return (
    <div className="rounded-lg border border-border bg-background/60 p-3">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* O que aconteceu na página */}
        <div>
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">
            Na página
          </div>
          <ol className="flex flex-col gap-1.5">
            {dados.eventos.length === 0 && <li className="text-[11px] text-muted-foreground">Sem registros.</li>}
            {dados.eventos.map((e, i) => (
              <li key={i} className="flex items-baseline gap-2 text-[11px]">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500/70" />
                <span className="text-foreground">
                  {NA_PAGINA[e.type] || e.type}
                  {e.type === 'step' && e.step != null ? ` ${e.step}` : ''}
                </span>
                <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">{quando(e.created_at)}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* O que a líder reportou */}
        <div>
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">
            Acompanhamento da líder
          </div>

          {!f ? (
            <p className="text-[11px] text-muted-foreground">
              Ainda sem acompanhamento aberto para este lead.
            </p>
          ) : (
            <>
              <ol className="flex flex-col gap-1.5">
                {ETAPAS.map(({ chave, titulo }) => {
                  const v = (f as unknown as Record<string, string | null>)[chave]
                  const r = v ? RESPOSTA[v] : null
                  return (
                    <li key={chave} className="flex items-baseline gap-2 text-[11px]">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${v ? 'bg-emerald-500/80' : 'bg-border'}`} />
                      <span className={v ? 'text-foreground' : 'text-muted-foreground'}>{titulo}</span>
                      {r && <span className={`${r.cor} font-medium`}>{r.texto}</span>}
                      {chave === 'contacted' && f.contacted_at && (
                        <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">{quando(f.contacted_at)}</span>
                      )}
                    </li>
                  )
                })}
                {f.converted === 1 && (
                  <li className="flex items-baseline gap-2 text-[11px]">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                    <span className="font-medium text-emerald-500">Virou consultora</span>
                    <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">{quando(f.converted_at)}</span>
                  </li>
                )}
              </ol>

              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 border-t border-border/60 pt-2 text-[10px] text-muted-foreground">
                {f.leader_name && <span>Líder: {f.leader_name}</span>}
                {f.status && <span>Status: {f.status}</span>}
                {f.nudge_count ? <span>{f.nudge_count} cobrança{f.nudge_count > 1 ? 's' : ''}</span> : null}
                {f.deadline_at && <span>Prazo: {quando(f.deadline_at)}</span>}
              </div>

              {dados.etapas.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[10px] text-muted-foreground hover:text-foreground">
                    {dados.etapas.length} evento{dados.etapas.length > 1 ? 's' : ''} de acompanhamento
                  </summary>
                  <ol className="mt-1.5 flex flex-col gap-1">
                    {dados.etapas.map((e, i) => (
                      <li key={i} className="flex items-baseline gap-2 text-[10px] text-muted-foreground">
                        <span className="font-mono">{e.type}</span>
                        {e.actor && <span>· {e.actor}</span>}
                        <span className="ml-auto shrink-0 tabular-nums">{quando(e.ts)}</span>
                      </li>
                    ))}
                  </ol>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
